import { useCallback, useEffect, useLayoutEffect, useRef } from 'react';
import { useAuth } from 'contexts/AuthContext';
import type { NativeCallState } from './nativeBridge';
import { currentViewportRevision, sameNativeCallScope, type NativeViewportReceipt } from './nativeVideoViewport';

export const PIP_ERROR = 'Picture-in-picture could not start. Keep the call open and try again.';

/** An explicit click is an intent, not permission to start against a hidden source view. */
export function useNativePictureInPicture({ state, stateRef, ownershipRevision, blocked, command }: {
  state: NativeCallState; stateRef: { current: NativeCallState }; blocked: boolean;
  ownershipRevision: { current: number };
  command: (name: string) => Promise<unknown>;
}) {
  const { user, isAuthLoading } = useAuth();
  const scope = useRef({ userId: user?.id, isAuthLoading, revision: 0 });
  if (scope.current.userId !== user?.id || scope.current.isAuthLoading !== isAuthLoading) {
    scope.current = { userId: user?.id, isAuthLoading, revision: scope.current.revision + 1 };
  }
  const intent = useRef<{ state: NativeCallState; ownerRevision: number; authRevision: number; afterViewport: number }>();
  const cancel = useCallback(() => { intent.current = undefined; }, []);
  useLayoutEffect(() => {
    if (blocked || isAuthLoading || state.state !== 'joined' || state.accessVerifying ||
        intent.current?.ownerRevision !== ownershipRevision.current || intent.current?.authRevision !== scope.current.revision) cancel();
  }, [blocked, cancel, isAuthLoading, ownershipRevision, state, user?.id]);
  useEffect(() => {
    const visibility = () => { if (document.visibilityState !== 'visible') cancel(); };
    document.addEventListener('visibilitychange', visibility);
    return () => { cancel(); document.removeEventListener('visibilitychange', visibility); };
  }, [cancel]);
  const request = useCallback(() => {
    const current = stateRef.current;
    if (scope.current.isAuthLoading || current.state !== 'joined' || current.accessVerifying || document.visibilityState !== 'visible') return;
    intent.current = { state: current, ownerRevision: ownershipRevision.current, authRevision: scope.current.revision, afterViewport: currentViewportRevision() };
  }, [ownershipRevision, stateRef]);
  const onViewportAcknowledged = useCallback((receipt: NativeViewportReceipt) => {
    const pending = intent.current;
    if (!pending) return;
    if (!sameNativeCallScope(pending.state, stateRef.current) || pending.ownerRevision !== ownershipRevision.current ||
        pending.authRevision !== scope.current.revision || scope.current.isAuthLoading) { cancel(); return; }
    if (receipt.meetingId !== pending.state.meetingId || receipt.generation !== pending.state.generation ||
        receipt.revision <= pending.afterViewport || receipt.revision !== currentViewportRevision() || document.visibilityState !== 'visible') return;
    cancel();
    void command('enterPictureInPicture').catch(() => undefined);
  }, [cancel, command, ownershipRevision, stateRef]);
  return { request, cancel, onViewportAcknowledged };
}
